/**
 * Store Failure Mapping
 *
 * What a store tells the user when an action fails. Every store used to map
 * a rejection to a message by hand — `error instanceof Error ? error.message
 * : fallback`, copied into each `catch` — which left no one place to say
 * that some rejections have nobody to tell.
 *
 * There is one such rejection: `SessionEndedError`. The API client raises it
 * when a response settles after the session that asked has ended, so the
 * store it would be written into now belongs to another account, or to
 * nobody. The right message for that is none at all.
 */

import { isSessionEndedError } from '../types/api/errors';

/**
 * The message to record for a failed action, or `null` when the session
 * that asked is over and there is nobody to show one to.
 *
 * Writing `null` into an error slot is what "no error" already looks like,
 * so a `catch` needs no branch: `set({ fetchError: failureMessage(error,
 * 'Failed to fetch') })` records the failure or, for an ended session,
 * records nothing.
 */
export const failureMessage = (
  error: unknown,
  fallback: string
): string | null => {
  if (isSessionEndedError(error)) return null;
  return error instanceof Error ? error.message : fallback;
};
